import { ArrowRightIcon } from "@phosphor-icons/react";
import { Button } from "react-aria-components";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export interface SuggestionChip {
  /** Visible button text (short, imperative). */
  label: string;
  /** Full prompt sent to the chat when clicked. */
  prompt: string;
}

interface SuggestionChipsProps {
  chips: SuggestionChip[];
  onSubmit: (text: string) => void;
}

/** Labels longer than this are capped at 30ch, truncated, and get a full-label tooltip. */
const MAX_CHIP_LABEL_CHARS = 30;

const chipButtonClassName =
  "inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[12.5px] font-medium text-gray-700 shadow-sm transition-colors outline-none hover:bg-gray-100 hover:text-gray-900 data-focus-visible:outline-none data-focus-visible:ring-2 data-focus-visible:ring-blue-500 data-focus-visible:ring-offset-0";

/** Row of clickable pill buttons that inject a follow-up prompt into the chat. */
const SuggestionChips = ({ chips, onSubmit }: SuggestionChipsProps) => {
  if (chips.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2 pt-1">
      {chips.map((chip) => {
        const isLongLabel = chip.label.length > MAX_CHIP_LABEL_CHARS;
        return (
          <TooltipTrigger
            key={`${chip.label}|${chip.prompt}`}
            isDisabled={!isLongLabel}
          >
            <Button
              type="button"
              onPress={() => {
                onSubmit(chip.prompt);
              }}
              className={chipButtonClassName}
            >
              <span
                className={cn(
                  "min-w-0 overflow-hidden text-ellipsis whitespace-nowrap",
                  isLongLabel && "max-w-[30ch]",
                )}
              >
                {chip.label}
              </span>
              <ArrowRightIcon className="h-3 w-3 shrink-0" weight="bold" />
            </Button>
            <Tooltip
              placement="top"
              offset={8}
              align="left"
              className="max-w-xs whitespace-normal wrap-break-word"
            >
              {chip.label}
            </Tooltip>
          </TooltipTrigger>
        );
      })}
    </div>
  );
};

export default SuggestionChips;
